import { MILLISECONDS } from '@chiroup/core/constants/time';
import { ChiroUpJSON } from '@chiroup/core/functions/ChiroUpJSON';
import { Modalities } from '@chiroup/core/types/Modalities.type';
import { use } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { MeContext } from '../contexts/me.context';
import { useSandboxClinicalData } from '../contexts/sandboxClinicalData.context';
import modalities from '../services/modality.service';

type Props = {
  ids?: number[];
  refetchOnMount?: boolean;
  enabled?: boolean;
  getReferenceCodes?: boolean;
  includeIds?: number[];
};

const useModalities = ({
  refetchOnMount = true,
  ids,
  includeIds = [],
  enabled = true,
  getReferenceCodes = false,
}: Props = {}) => {
  const queryClient = useQueryClient();
  const sandbox = useSandboxClinicalData();
  const meContext = use(MeContext);
  // Normalize query key: keep ids/getReferenceCodes; exclude includeIds so plan changes don't refetch
  const normalizedIds = ids && ids.length > 0 ? ids : undefined;
  const queryKey = [
    'modality',
    meContext.me.selectedClinic?.ID || -1,
    normalizedIds,
    getReferenceCodes,
  ];
  const { data, isFetching, refetch } = useQuery<{
    data: Modalities[];
  }>(
    queryKey,
    async () => {
      if (sandbox) return { data: [] };

      return await modalities.list(
        meContext.me.selectedClinic?.ID || -1,
        normalizedIds || [],
        getReferenceCodes
      );
    },
    {
      refetchOnWindowFocus: false,
      refetchOnMount,
      enabled,
      staleTime: MILLISECONDS.inMinutes(30),
      keepPreviousData: true,
    }
  );

  // On plan changes, fetch only missing includeIds and merge into cache to avoid spinners/refetch
  const clinicId = meContext.me.selectedClinic?.ID || -1;
  const currentIds = new Set(((data?.data || []) as Modalities[]).map((m: Modalities) => m.ID));

  const missing = (includeIds || []).filter(id => !currentIds.has(id));

  if (!sandbox && missing.length > 0 && clinicId !== -1) {
    // Fire-and-forget background: call list with includeIds=missing and merge
    //silently getting any missing modalities if needed. prevents spinners in plan anytime the plan changes.
    modalities
      .list(clinicId, [], getReferenceCodes, missing)
      .then((resp: { data: Modalities[] }) => {
        const extras: Modalities[] = Array.isArray(resp?.data) ? (resp.data as Modalities[]) : [];
        queryClient.setQueryData(queryKey, (prev: { data?: Modalities[] } | undefined) => {
          const prevArr: Modalities[] = Array.isArray(prev?.data)
            ? (prev!.data as Modalities[])
            : [];
          const byId = new Map<number, Modalities>();
          prevArr.forEach((it: Modalities) => byId.set(it.ID, it));
          (extras || []).forEach((it: Modalities) => byId.set(it.ID, it));

          return { ...(prev || {}), data: Array.from(byId.values()) } as { data: Modalities[] };
        });
      })
      .catch(() => {
        // swallow errors; the main list remains usable
      });
  }

  const updateClientData = (id: number, updateData: Partial<Modalities>) => {
    // Find and update all modality cache entries that might contain this ID
    // regardless of the current component state
    const modalityQueries = queryClient.getQueriesData(['modality']);

    modalityQueries.forEach(([cacheKey, queryData]) => {
      if (!queryData || typeof queryData !== 'object') return;

      const typedQueryData = queryData as Record<string, any>;

      // Case 1: Typical list response format: { data: Modalities[] }
      if (Array.isArray(typedQueryData.data)) {
        const index = typedQueryData.data.findIndex((item: any) => item.ID === id);

        if (index !== -1) {
          const newData = ChiroUpJSON.clone(typedQueryData);
          newData.data[index] = { ...newData.data[index], ...updateData };
          queryClient.setQueryData(cacheKey, newData);
        }
      }

      // Case 2: Nested response format: { data: { data: Modalities[] }}
      else if (typedQueryData.data && Array.isArray(typedQueryData.data.data)) {
        const index = typedQueryData.data.data.findIndex((item: any) => item.ID === id);

        if (index !== -1) {
          const newData = ChiroUpJSON.clone(typedQueryData);
          newData.data.data[index] = { ...newData.data.data[index], ...updateData };
          queryClient.setQueryData(cacheKey, newData);
        }
      }

      // Case 3: Single item response: { data: Modality }
      else if (typedQueryData.data?.ID === id) {
        const newData = ChiroUpJSON.clone(typedQueryData);
        newData.data = { ...newData.data, ...updateData };
        queryClient.setQueryData(cacheKey, newData);
      }
    });
  };

  return { isFetching, data, refetch, updateClientData };
};

export default useModalities;
